import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Key, 
  FileText,
  Database,
  Archive,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Navigate } from 'react-router-dom';

// Import existing page components to use as tab content
import SecurityPolicies from './SecurityPolicies';
import SecurityManagement from './SecurityManagement';
import LoginPolicies from './LoginPolicies';
import LoginPoliciesManagement from './LoginPoliciesManagement';
import AuditLogsManagement from './AuditLogsManagement';
import DataRetentionManagement from './DataRetentionManagement';

const UnifiedSecurity = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'policies';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync activeTab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'policies';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const tabs = [
    { 
      id: 'policies', 
      label: 'Security Policies', 
      icon: Shield,
      description: 'Manage security policies'
    },
    { 
      id: 'management', 
      label: 'Security Management', 
      icon: Lock,
      description: 'Security configuration and management'
    },
    { 
      id: 'login-policies', 
      label: 'Login Policies', 
      icon: Key,
      description: 'Authentication and login policies'
    },
    { 
      id: 'audit-logs', 
      label: 'Audit Logs', 
      icon: FileText,
      description: 'Security audit logs'
    },
    { 
      id: 'data-retention', 
      label: 'Data Retention', 
      icon: Archive,
      description: 'Data retention policies'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Security Management</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Comprehensive security configuration and monitoring
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Security Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'policies' && (
              <div>
                <SecurityPolicies />
              </div>
            )}

            {activeTab === 'management' && (
              <div>
                <SecurityManagement />
              </div>
            )}

            {activeTab === 'login-policies' && (
              <div>
                <LoginPolicies />
              </div>
            )}

            {activeTab === 'audit-logs' && (
              <div>
                <AuditLogsManagement />
              </div>
            )}

            {activeTab === 'data-retention' && (
              <div>
                <DataRetentionManagement />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedSecurity;

